<template>
  <div class="modal" tabindex="-1" role="dialog" backdrop="static" id="dashboardStatusDetailModal" @keyup.esc="onEsc($event)">
    <div class="modal-dialog modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header header">
          <h3 class="modal-title">{{ tileTitle }}</h3>
          <button type="button" class="btn-close btn-close-white" @click="close" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <p class="jump-hint">{{ $t('dashboardStatusDetailModal.jumpHint') }}</p>
          <table class="status-table">
            <tr v-for="entry in sortedEntries" :key="entry.key" class="status-row" @click="openEntry(entry)">
              <td class="dot-cell"><span class="status-dot" :style="{ backgroundColor: colorFor(entry.severity), border: entry.severity === 4 ? '1px solid #ced4da' : 'none' }"></span></td>
              <td class="name-cell">{{ entry.name }}</td>
              <td class="reason-cell">{{ reasonsText(entry) }}</td>
            </tr>
          </table>
          <p v-if="!sortedEntries.length" class="empty-hint">{{ $t('dashboardStatusDetailModal.empty') }}</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="close">{{ $t('dashboardStatusDetailModal.close') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from 'bootstrap'
import ModalViewMixin from '@/mixins/ModalViewMixin.js'
import { colorForSeverity, i18nKeyForReason, AlertReason } from '@/utils/monitoring-status'

//
// Detail view of one status tile: the FULL entry list (green ones too)
// with the alert reasons as text, worst first. Clicking a row jumps to
// the facility in the monitoring tab (openEntry -> panel -> HomePage).
//
export default {
  name: 'DashboardStatusDetailModal',
  mixins: [ModalViewMixin],
  props: {
    tile: {
      type: Object,
      default: null
    },
    entries: {
      type: Array,
      default: null
    }
  },
  emits: ['modalClosed', 'openEntry'],
  computed: {
    tileTitle () {
      return this.tile ? (this.tile.title || '') : ''
    },
    sortedEntries () {
      const list = (this.entries || []).slice()
      // worst first; disabled (4) at the very end, greens before them
      const rank = (entry) => entry.severity === 4 ? -1 : entry.severity
      list.sort((a, b) => rank(b) - rank(a))
      return list
    }
  },
  methods: {
    colorFor (severity) {
      return colorForSeverity(severity)
    },
    reasonsText (entry) {
      const reasons = (entry.reasons || []).filter(r => r !== AlertReason.NONE)
      if (reasons.length === 0) {
        return this.$t('sensorAlertReason.None')
      }
      return reasons.map(r => this.$t(i18nKeyForReason(r))).join(', ')
    },
    openEntry (entry) {
      this.$emit('openEntry', entry.facilityId)
    },
    close () {
      this.emitClosed()
      Modal.getInstance(document.getElementById('dashboardStatusDetailModal'))?.hide()
    }
  }
}
</script>

<style lang="scss" scoped>
.header {
  background-color: #6695c2;
  color: white;
}

.jump-hint {
  font-size: 0.85em;
  color: #6c757d;
}

.status-table {
  width: 100%;

  td {
    padding: 8px 10px;
    border-bottom: 1px solid var(--bs-border-color, #eee);
  }
}

.status-row {
  cursor: pointer;
}

.status-row:hover {
  background: rgba(102, 149, 194, 0.12);
}

.dot-cell {
  width: 28px;
}

.status-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.name-cell {
  font-weight: 600;
}

.reason-cell {
  color: #6c757d;
  font-size: 0.9em;
}

.empty-hint {
  color: #6c757d;
  text-align: center;
  padding: 16px 0;
}

[data-bs-theme="dark"] .jump-hint,
[data-bs-theme="dark"] .reason-cell,
[data-bs-theme="dark"] .empty-hint {
  color: #adb5bd;
}
</style>
